import React, { useState, useEffect } from 'react';
import { getRestaurantInfo, createContactMessage } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { MapPin, Mail, Clock, ExternalLink } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [restaurantInfo, setRestaurantInfo] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    // Load restaurant info from API
    const fetchData = async () => {
      try {
        const info = await getRestaurantInfo();
        setRestaurantInfo(info);
      } catch (error) {
        console.error('Error fetching restaurant info:', error);
        const savedInfo = localStorage.getItem('restaurantInfo');
        if (savedInfo) setRestaurantInfo(JSON.parse(savedInfo));
      }
    };
    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast({
        title: "Información incompleta",
        description: "Por favor completa todos los campos.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await createContactMessage(formData);
      toast({
        title: "¡Mensaje enviado!",
        description: "Gracias por contactarnos. Te responderemos pronto."
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "No se pudo enviar el mensaje. Intenta de nuevo.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!restaurantInfo) return null;

  return (
    <section id="contact" className="py-24 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2
            className="md:text-6xl !font-bold !text-5xl mb-4"
            style={{ fontFamily: "'Crimson Text', serif" }}>Póngase en contacto con nosotros


          </h2>
          <p className="max-w-2xl !text-lg mx-auto text-gray-600">¿Tienes preguntas o peticiones especiales? ¡Estamos aquí para ayudarte!

          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <Card style={{ backgroundColor: '#F5B8D0' }}>
              <CardHeader>
                <CardTitle className="!font-semibold !text-2xl" style={{ fontFamily: "'Crimson Text', serif" }}>Contacto

                </CardTitle>
                <CardDescription className="!text-sm text-muted-foreground">Póngase en contacto con nosotros a través de cualquiera de estos canales</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="w-6 h-6 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold mb-1">Dirección</h4>
                    <p className="text-gray-700">{restaurantInfo.address}</p>
                    {restaurantInfo.mapsUrl && (
                      <a 
                        href={restaurantInfo.mapsUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-pink-600 hover:text-pink-800 mt-2 font-medium"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Ver en Google Maps
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Mail className="w-6 h-6 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold mb-1">Email</h4>
                    <p className="text-gray-700">{restaurantInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Clock className="w-6 h-6 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold mb-1">Horario</h4>
                    <p className="text-gray-700">{restaurantInfo.hours.weekday}</p>
                    {restaurantInfo.hours.weekend && <p className="text-gray-700">{restaurantInfo.hours.weekend}</p>}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="bg-white/90 backdrop-blur-sm shadow-lg">
            <CardHeader>
              <CardTitle className="!font-semibold !text-2xl">Envianos un mensaje</CardTitle>
              <CardDescription className="!text-sm text-muted-foreground">Le responderemos en la mayor brevedad posible.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="contact-name" className="peer-disabled:cursor-not-allowed peer-disabled:opacity-70 !font-medium !text-sm">Nombre</Label>
                  <Input
                    id="contact-name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your name"
                    required />

                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-email">Email</Label>
                  <Input
                    id="contact-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    required />

                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject" className="peer-disabled:cursor-not-allowed peer-disabled:opacity-70 !font-medium !text-sm">Asunto</Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="What's this about?"
                    required />

                </div>
                <div className="space-y-2">
                  <Label htmlFor="message" className="peer-disabled:cursor-not-allowed peer-disabled:opacity-70 !font-medium !text-sm">Mensaje</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tu mensaje..."
                    rows={5}
                    required />

                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-9 w-full hover:bg-gray-900 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg !font-medium !text-lg gap-2 px-4 py-6 rounded-md shadow text-white bg-black">
                  {isSubmitting ? 'Enviando...' : 'Enviar'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>);

};

export default Contact;